import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import { getFacilityById, updateFacility } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import {
  ArrowLeft,
  MapPin,
  Building2,
  Wifi,
  Car,
  FileText,
  Tag,
  Image,
  Upload,
  Save,
  Eye,
  AlertCircle,
  Coffee,
  Monitor,
  Users,
  Clock,
  Calendar,
  Settings,
} from "lucide-react";
import { useLocationContext } from "../../../client/contexts/LocationContext";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../../components/layout";
import { useRequireSectionVisible } from "../../../client/hooks/useRequireSectionVisible";
import { AudienceSelect, toAudience, type Audience } from "../../../client/components/AudienceSelect";

interface FacilityFormData {
  title: string;
  description: string;
  contentBody: string;
  category: string;
  facilityType: string;
  tags: string;
  images: string[];
  visibility: Audience;
  isPinned: boolean;
  requiresApproval: boolean;
  expiryDate: string;
  isAvailable: boolean;
  reservationRequired: boolean;
  operatingHours: string;
  location: string;
  contact: string;
}

const FACILITY_CATEGORIES = [
  { value: "vending", label: "Vending Machines", icon: Coffee },
  { value: "equipment", label: "Equipment", icon: Monitor },
  { value: "rooms", label: "Rooms", icon: Building2 },
  { value: "coworking", label: "Co-Working Spaces", icon: Users },
  { value: "general", label: "General", icon: MapPin },
];

const FACILITY_TYPES = [
  {
    value: "available",
    label: "Available",
    description: "Freely available for use",
  },
  {
    value: "reservable",
    label: "Reservable",
    description: "Requires advance reservation",
  },
  {
    value: "walkin",
    label: "Walk-in Only",
    description: "First come, first served",
  },
];



export default function EditFacilityPage() {
  useRequireSectionVisible("showFacility");
  const { facilityId } = useParams<{ facilityId: string }>();
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<FacilityFormData>({
    title: "",
    description: "",
    contentBody: "",
    category: "general",
    facilityType: "available",
    tags: "",
    images: [],
    visibility: "VERIFIED",
    isPinned: false,
    requiresApproval: false,
    expiryDate: "",
    isAvailable: true,
    reservationRequired: false,
    operatingHours: "",
    location: "",
    contact: "",
  });

  // Fetch facility data
  const {
    data: facility,
    isLoading: isFacilityLoading,
    error: facilityError,
  } = useQuery(getFacilityById, { facilityId: facilityId! });

  // Populate form data when facility is loaded
  useEffect(() => {
    if (facility) {
      setFormData({
        title: facility.title || "",
        description: facility.description || "",
        contentBody: facility.contentBody || "",
        category: facility.category || "general",
        facilityType: facility.metadata?.facilityType || "available",
        tags: facility.tags?.join(", ") || "",
        images: facility.images || [],
        visibility: toAudience(facility.visibility),
        isPinned: facility.isPinned || false,
        requiresApproval: facility.requiresApproval || false,
        expiryDate: facility.expiryDate
          ? new Date(facility.expiryDate).toISOString().split("T")[0]
          : "",
        isAvailable: facility.metadata?.isAvailable !== false,
        reservationRequired: facility.metadata?.reservationRequired || false,
        operatingHours: facility.metadata?.operatingHours || "",
        location: facility.metadata?.facilityLocation || "",
        contact: facility.metadata?.contact || "",
      });
    }
  }, [facility]);

  const handleChange = (field: keyof FacilityFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const updateData = {
        facilityId: facilityId!,
        title: formData.title.trim(),
        description: formData.description.trim(),
        contentBody: formData.contentBody.trim(),
        visibility: formData.visibility,
        category: formData.category,
        facilityType: formData.facilityType,
        tags: formData.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
        images: formData.images,
        isPinned: formData.isPinned,
        metadata: {
          facilityType: formData.facilityType,
          isAvailable: formData.isAvailable,
          reservationRequired: formData.reservationRequired,
          operatingHours: formData.operatingHours,
          facilityLocation: formData.location,
          contact: formData.contact,
        },
      };

      await updateFacility(updateData);

      // Navigate back to facility detail page
      if (selectedLocation?.locationCode) {
        navigate(
          `/locations/${selectedLocation.locationCode}/facilities/${facilityId}`,
        );
      } else {
        navigate(`/facilities/${facilityId}`);
      }
    } catch (error) {
      console.error("Error updating facility:", error);
      setErrors({ submit: "Failed to update facility. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (selectedLocation?.locationCode) {
      navigate(
        `/locations/${selectedLocation.locationCode}/facilities/${facilityId}`,
      );
    } else {
      navigate(`/facilities/${facilityId}`);
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">Please log in to edit this facility.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (isFacilityLoading) {
    return (
      <IndividualEditPageLayout title="Loading Facility...">
        <div className="p-6 text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading facility details...</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (facilityError || !facility) {
    return (
      <IndividualEditPageLayout title="Facility Not Found" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Facility Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The facility you're looking for doesn't exist or you don't have
            permission to edit it.
          </p>
          <button
            onClick={handleCancel}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton
        type="submit"
        form="facility-form"
        disabled={isSubmitting}
        icon={Save}
      >
        {isSubmitting ? "Updating..." : "Save Changes"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleCancel} disabled={isSubmitting}>
        Cancel
      </ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Facility Status" hideOnMobile>
        <div className="space-y-4">
          <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
            <input
              type="checkbox"
              checked={formData.isAvailable}
              onChange={(e) => handleChange("isAvailable", e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-bold text-slate-700">Currently Available</span>
          </label>

          <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
            <input
              type="checkbox"
              checked={formData.reservationRequired}
              onChange={(e) => handleChange("reservationRequired", e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-bold text-slate-700">Reservation Required</span>
          </label>
        </div>
      </SidebarSection>

      <SidebarSection title="Display Options" hideOnMobile>
        <div className="space-y-4">
          <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
            <input
              type="checkbox"
              checked={formData.isPinned}
              onChange={(e) => handleChange("isPinned", e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-bold text-slate-700">Pin to Top</span>
          </label>
          <label className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors">
            <input
              type="checkbox"
              checked={formData.requiresApproval}
              onChange={(e) => handleChange("requiresApproval", e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-bold text-slate-700">Requires Approval</span>
          </label>
        </div>
      </SidebarSection>

      <SidebarSection title="Location Info" hideOnMobile>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Specific Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleChange("location", e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
              placeholder="e.g., Level 2, Room 204"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Expiry Date</label>
            <input
              type="date"
              value={formData.expiryDate}
              onChange={(e) => handleChange("expiryDate", e.target.value)}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
            />
          </div>
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Edit Facility"
      subtitle={`Location: ${facility.location?.name || selectedLocation?.name || "No location selected"}`}
      icon={Settings}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="facility-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Facility Name</label>
            <div className="relative">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange("title", e.target.value)}
                className={`w-full pl-10 pr-4 py-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium ${errors.title ? "border-red-300 bg-red-50" : "border-slate-200"}`}
                placeholder="e.g., Snack Vending Machine"
                required
              />
            </div>
            {errors.title && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1 font-medium">
                <AlertCircle className="w-4 h-4" /> {errors.title}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Short Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={3}
              className={`w-full px-4 py-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium ${errors.description ? "border-red-300 bg-red-50" : "border-slate-200"}`}
              placeholder="Brief summary..."
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Building2 className="w-4 h-4 text-blue-600" /> Categorization</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange("category", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                >
                  {FACILITY_CATEGORIES.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Access Type</label>
                <select
                  value={formData.facilityType}
                  onChange={(e) => handleChange("facilityType", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                >
                  {FACILITY_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Clock className="w-4 h-4 text-blue-600" /> Usage Details</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Operating Hours</label>
                <input
                  type="text"
                  value={formData.operatingHours}
                  onChange={(e) => handleChange("operatingHours", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                  placeholder="e.g., 24/7"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Contact Information</label>
                <input
                  type="text"
                  value={formData.contact}
                  onChange={(e) => handleChange("contact", e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
                  placeholder="Phone, email, etc."
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><FileText className="w-4 h-4 text-blue-600" /> Detailed Information</h3>
          <textarea
            value={formData.contentBody}
            onChange={(e) => handleChange("contentBody", e.target.value)}
            rows={8}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
            placeholder="Usage instructions, list of features, etc."
          />
        </div>

        <div className="space-y-4">
          <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Tags (comma separated)</label>
          <input
            type="text"
            value={formData.tags}
            onChange={(e) => handleChange("tags", e.target.value)}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
            placeholder="e.g., quiet, wifi"
          />
        </div>

        <AudienceSelect
          value={formData.visibility}
          onChange={(value) => handleChange("visibility", value)}
        />

        {errors.submit && <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm font-medium">{errors.submit}</div>}
      </form>
    </IndividualEditPageLayout>
  );
}
